import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { userFields } from '@openx/components-administration/lib/constants.ts';
import { useAdministrationContext } from '@openx/components-administration/lib/utils/context/AdministrationContext.tsx';
import { CreateUserMutationType } from '@openx/components-administration/lib/utils/context/UserHookTypes.ts';
import type { UserFormData, ValidatedUserFormData } from '@openx/components-administration/types.ts';
import { FormActions } from '@openx/components-core/lib/FormActions/FormActions.tsx';
import { FormContainer } from '@openx/components-core/lib/FormContainer/FormContainer.tsx';
import { scrollToError } from '@openx/components-core/utils/scrollToError.ts';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import { OPEN_AUDIENCE_ORG_EXTERNAL_ID } from '@openx/utils/services/config.ts';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { UserAccessSection } from './sections/UserAccessSection.tsx';
import { UserDetailsSection } from './sections/UserDetailsSection.tsx';
import { getValidationSchema } from './validationSchema.ts';

export const initialValues: UserFormData = {
  [userFields.ACCOUNT]: null,
  [userFields.EMAIL]: '',
  [userFields.FIRST_NAME]: '',
  [userFields.LAST_NAME]: '',
  [userFields.ROLE]: null,
};

export const UserCreateForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { useCreateUser } = useAdministrationContext();
  const { createUser } = useCreateUser();

  const formMethods = useForm<UserFormData, unknown, ValidatedUserFormData>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: yupResolver(getValidationSchema(t)),
  });

  const onSubmit = async (data: ValidatedUserFormData) => {
    const type =
      data.account?.id !== OPEN_AUDIENCE_ORG_EXTERNAL_ID ? CreateUserMutationType.PARENT : CreateUserMutationType.ROOT;

    await createUser({
      type,
      user: {
        email_address: data[userFields.EMAIL],
        first_name: data[userFields.FIRST_NAME]?.trim(),
        last_name: data[userFields.LAST_NAME]?.trim(),
        parent_account_id: data[userFields.ACCOUNT].id,
        role: data.role,
      },
    });
  };

  return (
    <PageHeader title={t('Create New User')} titlePrefix="OpenXSelect">
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit, scrollToError)}>
          <FormContainer fullPage>
            <Box>
              <UserDetailsSection />
              <UserAccessSection />
            </Box>

            <FormActions
              submitButtonLabel={t('Send Invite')}
              cancelButtonLabel={t('Cancel')}
              onCancel={() => navigate('/users')}
              isSubmitting={formMethods.formState.isSubmitting}
            />
          </FormContainer>
        </form>
      </FormProvider>
    </PageHeader>
  );
};
